import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog.jsx';
import { useFilters } from '../context/FilterContext.js';
import { ShieldCheck, Crown, Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ROLE_LABEL = { admin: 'Administrador G2', manager: 'Empresa Gestora', respondent: 'Respondente' };
const ROLE_BADGE = {
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  manager: 'bg-violet-100 text-violet-700 border-violet-200',
  respondent: 'bg-slate-100 text-slate-600 border-slate-200',
};

const Empresas = () => {
  const [companies, setCompanies] = useState([]);
  const [isG2Admin, setIsG2Admin] = useState(false);
  const { filters } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/companies/list`, auth());
      setCompanies(data.companies || []);
      setIsG2Admin(!!data.is_g2_admin);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load, filters]);

  const updateRole = async (companyId, newRole) => {
    try {
      await axios.patch(`${API}/companies/${companyId}/role`, { role: newRole }, auth());
      toast.success('Perfil atualizado');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao atualizar perfil'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteScope, setDeleteScope] = useState('indicators');
  const [deleting, setDeleting] = useState(false);

  const openDelete = (c) => { setDeleteModal(c); setDeleteScope('indicators'); };

  const handleDelete = async () => {
    if (!deleteModal) return;
    const labels = {
      indicators: 'TODOS os indicadores (RH, ESG, Salários, Benefícios) desta empresa',
      responses: 'TODAS as respostas de pesquisas desta empresa',
      all: 'A EMPRESA INTEIRA — indicadores, respostas e o cadastro',
    };
    if (!window.confirm(`Tem certeza?\n\nVai apagar ${labels[deleteScope]}.\n\nEmpresa: ${deleteModal.razao_social}\n\nEsta ação é IRREVERSÍVEL.`)) return;
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${API}/admin/companies/${deleteModal.id}/data?scope=${deleteScope}`, auth());
      toast.success(`Removido: ${JSON.stringify(data.removed)}`);
      setDeleteModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao apagar dados');
    } finally { setDeleting(false); }
  };

  return (
    <MainLayout title="Empresas Participantes" userRole={userRole}>
      <div className="space-y-6" data-testid="empresas-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-slate-500">
            {companies.length} empresas cadastradas
            {isG2Admin && (
              <span className="ml-2 inline-flex items-center gap-1 text-blue-600 font-medium" data-testid="g2-admin-badge">
                <Crown className="w-3 h-3" /> Você pode editar perfis
              </span>
            )}
          </p>
          {/* Resumo por perfil */}
          <div className="flex gap-2 text-xs">
            {['admin', 'manager', 'respondent'].map(r => (
              <span key={r} className={`px-2 py-1 rounded-full border ${ROLE_BADGE[r]}`} data-testid={`role-summary-${r}`}>
                {ROLE_LABEL[r]}: {companies.filter(c => c.role === r).length}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`company-card-${c.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-sm font-semibold text-slate-900 leading-tight">{c.razao_social}</CardTitle>
                  {!isG2Admin && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${ROLE_BADGE[c.role] || ROLE_BADGE.respondent}`}>
                      {ROLE_LABEL[c.role] || 'Respondente'}
                    </span>
                  )}
                </div>
                {c.email && <p className="text-[10px] text-slate-400 mt-1 truncate">{c.email}</p>}
              </CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">CNPJ</span><span className="text-slate-700">{c.cnpj}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Segmento</span><span className="text-slate-700">{c.segmento}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Região</span><span className="text-slate-700 capitalize">{c.regiao}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Porte</span><span className="text-slate-700 capitalize">{c.porte}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Funcionários</span><span className="font-medium text-slate-900">{c.total_funcionarios?.toLocaleString('pt-BR')}</span></div>

                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ShieldCheck className="w-3 h-3 text-slate-400" />
                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Perfil de Acesso</span>
                  </div>
                  {isG2Admin ? (
                    <Select value={c.role} onValueChange={(v) => updateRole(c.id, v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`role-select-${c.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador G2</SelectItem>
                        <SelectItem value="manager">Empresa Gestora</SelectItem>
                        <SelectItem value="respondent">Respondente</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`inline-flex items-center text-xs px-2 py-1 rounded border font-medium ${ROLE_BADGE[c.role] || ROLE_BADGE.respondent}`} data-testid={`role-readonly-${c.id}`}>
                      {ROLE_LABEL[c.role] || 'Respondente'}
                    </span>
                  )}
                </div>

                {isG2Admin && c.email !== 'g2consultoriarh@gmail.com' && (
                  <div className="pt-2 border-t border-slate-100">
                    <button
                      onClick={() => openDelete(c)}
                      className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 rounded py-1.5 transition-colors"
                      data-testid={`delete-company-btn-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Apagar dados desta empresa
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* DELETE COMPANY DATA MODAL */}
        <Dialog open={!!deleteModal} onOpenChange={(o) => !o && setDeleteModal(null)}>
          <DialogContent className="max-w-md" data-testid="delete-data-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" /> Apagar dados — {deleteModal?.razao_social}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Escolha quais dados desta empresa serão apagados. Ação irreversível.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors ${deleteScope === 'indicators' ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" checked={deleteScope === 'indicators'} onChange={() => setDeleteScope('indicators')} data-testid="scope-indicators" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">Apenas indicadores</p>
                  <p className="text-xs text-slate-500">Apaga RH, ESG, Salários e Benefícios. Mantém empresa cadastrada e respostas de pesquisas.</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors ${deleteScope === 'responses' ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" checked={deleteScope === 'responses'} onChange={() => setDeleteScope('responses')} data-testid="scope-responses" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">Apenas respostas de pesquisas</p>
                  <p className="text-xs text-slate-500">Apaga as respostas das pesquisas. Mantém indicadores e empresa.</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors ${deleteScope === 'all' ? 'border-red-400 bg-red-100' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" checked={deleteScope === 'all'} onChange={() => setDeleteScope('all')} data-testid="scope-all" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-700">Tudo — empresa + indicadores + respostas</p>
                  <p className="text-xs text-red-600">Remove o cadastro da empresa, todos os indicadores e respostas. Irreversível.</p>
                </div>
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setDeleteModal(null)} data-testid="delete-cancel-btn">Cancelar</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={deleting} onClick={handleDelete} data-testid="delete-confirm-btn">
                  {deleting ? 'Apagando...' : 'Confirmar exclusão'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Empresas;
